import { Injectable } from '@angular/core';
import { LoggerService } from '../../../core/logger.service';
import { StorageService } from '../../../core/storage/storage.service';
import { SyncCryptService } from '../../../core/crypt/sync-crypt.service';
import { TransferItemService } from '../queue/transfer-item.service';

import { DownloadWorkerService } from './download-worker.service';
import { DownloadLegacyService } from './download-legacy.service';
import { BrowserSupportService } from '../../../core/browser-support.service';
import { ApiService } from '../../../core/api.service';
import { CryptEngine } from '../../../shared/models/crypt-engine.model';
import {
  IDownloadMethod,
  IRenderData,
  IRenderFile,
  IStorageMethod,
  IStoreData,
  ITransferItem,
  ITransferItemDownload,
} from 'src/sync';
import { TRANSFER_STATUS } from '../../models/transfer.model';
import { DownloadChunkFetchOptions } from './download-chunk-fetch.service';
import { TransferType } from '../../models/transfer-type';
import { ONE_MB } from '../../../shared/shared.constants';
import { ERROR_CODES, ErrCode, StreamingSaveSeam } from '../../../shared/models';
import { StreamingSaveService } from '../../../core/storage/streaming-save.service';
import { StreamingSaveTelemetryService, streamingOutcomeForStatus } from '../streaming-save-telemetry.service';
import { DOWNLOAD_CHUNK_PROCESS_MAX_RETRIES } from '../../transfer.constants';
import { isRetryableDownloadError } from './download-chunk-fetch.service';

type DownloadRejection = { errcode?: number; code?: number };

@Injectable({
  providedIn: 'root',
})
export class DownloadService {
  public savemethod: string;
  public chunksize = 25 * ONE_MB;
  private downloader: IDownloadMethod;
  private storage: IStorageMethod;

  constructor(
    private browser: BrowserSupportService,
    private crypt: SyncCryptService,
    private downloadLegacy: DownloadLegacyService,
    private downloadWorker: DownloadWorkerService,
    private log: LoggerService,
    private storageService: StorageService,
    private transferItem: TransferItemService,
    private api: ApiService,
    private streamingSaveTelemetryService: StreamingSaveTelemetryService,
    private streamingSaveService: StreamingSaveService,
  ) {
    this.init();
  }

  public async downloadCompat(tItem: ITransferItemDownload): Promise<ITransferItemDownload> {
    await this.storageService.prepare();
    return this.downloadPlain(this.storageService.getMemoryStorage(), tItem);
  }

  public async downloadPreview(tItem: ITransferItemDownload): Promise<ITransferItemDownload> {
    await this.storageService.prepare();
    return this.download(this.storageService.getMemoryStorage(), tItem);
  }

  public async downloadItem(tItem: ITransferItemDownload): Promise<ITransferItemDownload> {
    await this.storageService.prepare();
    return this.download(this.storageService.getDefaultStorage(tItem), tItem);
  }

  public async saveThumb(tItem: ITransferItemDownload, bytes: ArrayBuffer): Promise<ITransferItem> {
    const fileSize =
      bytes.byteLength - Math.ceil(bytes.byteLength / this.crypt.GCM_PACKET_SIZE) * this.crypt.GCM_TAG_OVERHEAD;
    try {
      const start = Date.now();
      this.storage.init({
        dirname: 'syncpreview',
        filesize: fileSize,
        filename: tItem.filename,
        mimetype: tItem.mimetype,
        cachekey: tItem.cachekey,
        blobtype: tItem.blobtype,
        uniqid: tItem.uniqid,
        path: '',
      });
      this.log.d('  - ' + (Date.now() - start) + ' ms to initialize - resolved');
      const decStart = Date.now();
      const data = await this.crypt.filedataDecrypt(new Uint8Array(bytes), tItem.data_key, 0);
      const decEnd = Date.now();
      this.log.d('  - ' + (decEnd - decStart) + ' ms to decrypt data');

      const dlData = {
        bytearray: data,
        offset: 0,
        reqlength: 0,
        chunkLength: bytes.byteLength,
        tItem: tItem,
      };
      const packetAmt = Math.ceil(dlData.offset / this.crypt.GCM_PACKET_SIZE),
        gcmOffset = packetAmt * this.crypt.GCM_TAG_OVERHEAD;
      let writeOffset = dlData.offset - gcmOffset;
      if (writeOffset < 0) {
        writeOffset = 0;
      }
      const wr = Date.now();
      await this.storage.writeChunk(dlData.tItem, dlData.bytearray, writeOffset);
      const we = Date.now();
      this.log.d('  - ' + (we - wr) + ' ms of ThumbStore.writeChunk()');
      const renderData = await this.storage.getRenderData(tItem);
      const re = Date.now();
      this.log.d('  - ' + (re - we) + ' ms of ThumbStore.getRenderData()');

      const renderFile = await this.prepareRenderData(renderData);
      const pe = Date.now();
      this.log.d('  - ' + (pe - re) + ' ms of prepareRenderData()');

      tItem.renderFile = renderFile;
      tItem.status = TRANSFER_STATUS.STATUS_SUCCESS;
      this.log.d('  - ' + (Date.now() - start) + ' ms to saveThumb()');
    } catch (err) {
      tItem.status = this.statusFromError(err);
      this.log.error('Caught an error in saveData for thumbs');
      this.log.error(err);
    }
    return tItem;
  }

  private async init(): Promise<void> {
    let downloader: IDownloadMethod;
    switch (this.browser.getEncryptionEngine()) {
      case CryptEngine.NATIVE:
        downloader = this.downloadWorker;
        break;
      case CryptEngine.BUFFER:
        downloader = this.downloadWorker;
        break;
      case CryptEngine.LEGACY:
        downloader = this.downloadLegacy;
        break;
      default:
        downloader = this.downloadLegacy;
        break;
    }
    this.downloader = downloader;
    this.log.info('TRANSFER download method = ' + this.browser.getEncryptionEngine());
    this.storage = this.storageService.getMemoryStorage();
  }

  private async download(store: IStorageMethod, tItem: ITransferItemDownload): Promise<ITransferItemDownload> {
    const stTime = Date.now();
    tItem = await this.checkDownload(store, tItem);
    if (tItem.status !== TRANSFER_STATUS.STATUS_WAITING) {
      this.log.warn('Item is not waiting, returning ' + tItem.status);
      // A destination was already picked, so this abandoned attempt still belongs in the funnel.
      await this.discardUnopenedDestination(store, tItem);
      this.trackStreamingOutcome(store, tItem, stTime);
      return tItem;
    }
    tItem.status = TRANSFER_STATUS.STATUS_WORKING;

    try {
      await store.init({
        dirname: 'syncpreview',
        filesize: tItem.filesize,
        filename: tItem.filename,
        mimetype: tItem.mimetype,
        cachekey: tItem.cachekey,
        blobtype: tItem.blobtype,
        uniqid: tItem.uniqid,
        path: '',
        streamingHandle: tItem.streamingHandle,
      });
    } catch (ex) {
      // Opening the writable is where a revoked permission or full disk surfaces; callers expect the throw.
      await this.discardUnopenedDestination(store, tItem);
      this.trackStreamingOutcome(store, tItem, stTime);
      throw ex;
    }

    try {
      tItem = await this.processChunks(store, tItem);
      if (tItem.status >= TRANSFER_STATUS.STATUS_ERROR) {
        return tItem;
      }
      // Cancelled (1002) sorts below STATUS_ERROR (3000), so the guard above does not cover it.
      if (store.isStreamingToDisk && this.isCancelled(tItem)) {
        return tItem;
      }
      if (store.isStreamingToDisk) {
        await store.finalize(tItem);
        tItem.savedToDisk = true;
      } else {
        const renderData = await store.getRenderData(tItem);
        const renderFile = this.prepareRenderData(renderData);
        tItem.renderFile = renderFile;
      }
      const timeTaken = (Date.now() - stTime) / 1000;
      const bps = tItem.filesize / timeTaken;
      this.log.info(`Download completed ${timeTaken} seconds ${bps / 1000} kbps`);
      tItem.status = TRANSFER_STATUS.STATUS_SUCCESS;
      this.downloader.completed(tItem);
    } catch (ex) {
      if (store.isStreamingToDisk) {
        await store.cancel(tItem, ex);
      }
      // processChunks marks the item SUCCESS before finalize/getRenderData run, so a throw must overwrite it.
      if (!this.isCancelled(tItem)) {
        tItem.status = TRANSFER_STATUS.STATUS_ERROR;
      }
    } finally {
      this.trackStreamingOutcome(store, tItem, stTime);
    }

    return tItem;
  }

  /**
   * Removes the 0-byte placeholder the picker truncated, on the paths where init() never opened a
   * session. Once it has, the store owns the handle and removing it would delete a committed download.
   */
  private async discardUnopenedDestination(store: IStorageMethod, tItem: ITransferItemDownload): Promise<void> {
    if (store.isStreamingToDisk && tItem.streamingHandle) {
      await this.streamingSaveService.discardDestination(tItem.streamingHandle);
    }
  }

  /**
   * The single-file seam's only completion measurement — DOWNLOAD_FILE is emitted at queue time and cannot serve.
   * Called from `finally` because processChunks() returns rather than throws, so failures bypass the catch.
   */
  private trackStreamingOutcome(store: IStorageMethod, tItem: ITransferItemDownload, startedAt: number): void {
    if (!store.isStreamingToDisk) {
      return;
    }
    this.streamingSaveTelemetryService.track({
      seam: StreamingSaveSeam.SINGLE_FILE,
      outcome: streamingOutcomeForStatus(tItem.status),
      totalSizeBytes: tItem.filesize,
      // Only a real error code, never a bare status: cancelled (1002) and working (10) have none.
      errorCode: tItem.status >= TRANSFER_STATUS.STATUS_ERROR ? tItem.status : undefined,
      isLink: !!tItem.linkID,
      durationMs: Date.now() - startedAt,
    });
  }

  private async downloadPlain(store: IStorageMethod, tItem: ITransferItemDownload): Promise<ITransferItemDownload> {
    const stTime = Date.now();
    tItem = await this.checkDownload(store, tItem);
    if (tItem.status !== TRANSFER_STATUS.STATUS_WAITING) {
      this.log.warn('Item is not waiting, returning ' + tItem.status);
      return tItem;
    }
    tItem.status = TRANSFER_STATUS.STATUS_WORKING;

    await store.init({
      dirname: 'syncpreview',
      filesize: tItem.filesize,
      filename: tItem.filename,
      mimetype: tItem.mimetype,
      cachekey: tItem.cachekey,
      blobtype: tItem.blobtype,
      uniqid: tItem.uniqid,
      path: '',
    });

    try {
      const result = await this.api.fetchFilePlain(tItem).catch((_err) => {
        this.log.error('An error occured fetching plain file');
        tItem.status = TRANSFER_STATUS.STATUS_ERROR;
      });

      if (result) {
        this.storeData(store, {
          bytearray: new Uint8Array(result, 0),

          // We get these values all at once, so no need to keep track of position
          offset: 0,
          reqlength: 0,
          chunkLength: 0,
          tItem: tItem,
        });
      } else {
        this.log.error('An error occured');
      }

      if (tItem.status >= TRANSFER_STATUS.STATUS_ERROR) {
        return tItem;
      }
      const renderData = await store.getRenderData(tItem);
      const renderFile = this.prepareRenderData(renderData);
      tItem.renderFile = renderFile;
      const timeTaken = (Date.now() - stTime) / 1000;
      const bps = tItem.filesize / timeTaken;
      this.log.info(`Download completed ${timeTaken} seconds ${bps / 1000} kbps`);
      tItem.status = TRANSFER_STATUS.STATUS_SUCCESS;
      this.downloader.completed(tItem);
    } catch (ex) {
      if (!tItem.status || tItem.status == TRANSFER_STATUS.STATUS_WORKING) {
        tItem.status = TRANSFER_STATUS.STATUS_ERROR;
      }
    }

    return tItem;
  }

  private async checkDownload(
    storageMethod: IStorageMethod,
    tItem: ITransferItemDownload,
  ): Promise<ITransferItemDownload> {
    const availableStorage = storageMethod.isStreamingToDisk ? undefined : await this.browser.getAvailableStorage();
    if (availableStorage !== undefined && tItem.filesize > availableStorage) {
      this.log.warn('Insufficient browser storage quota for this download');
      this.log.warn(' - filesize: ' + tItem.filesize);
      this.log.warn(' - Available browser storage: ' + availableStorage);
      this.log.warn(' - Save method: ' + this.browser.getFileSaveMethod());
      this.log.warn(' - StorageFactory method: ' + this.browser.getFileStorageMethod());
      tItem.status = TRANSFER_STATUS.STATUS_ERR_DISK_SPACE;
    } else if (storageMethod.maxsize > 0 && tItem.filesize > storageMethod.maxsize) {
      this.log.warn('File is too large for this browser');
      this.log.warn(' - filesize: ' + tItem.filesize);
      this.log.warn(' - Max StorageFactory size: ' + storageMethod.maxsize);
      this.log.warn(' - Save method: ' + this.browser.getFileSaveMethod());
      this.log.warn(' - StorageFactory method: ' + this.browser.getFileStorageMethod());
      tItem.status = TRANSFER_STATUS.STATUS_ERR_SIZE;
    } else if (!tItem.filedata.viewable && this.browser.getFileSaveMethod() == 'none') {
      this.log.warn('File cannot be opened in this browser');
      this.log.warn(' - filesize: ' + tItem.filesize);
      this.log.warn(' - Save method: ' + this.browser.getFileSaveMethod());
      this.log.warn(' - StorageFactory method: ' + this.browser.getFileStorageMethod());
      tItem.status = TRANSFER_STATUS.STATUS_ERR_OPEN;
    } else if (
      tItem.filedata.viewable &&
      this.browser.getFileSaveMethod() == 'none' &&
      tItem.type != TransferType.PREVIEW
    ) {
      this.log.warn('File save is incompatible with your browser');
      this.log.warn(' - filesize: ' + tItem.filesize);
      this.log.warn(' - Save method: ' + this.browser.getFileSaveMethod());
      this.log.warn(' - StorageFactory method: ' + this.browser.getFileStorageMethod());
      tItem.status = 3011;
    } else if (!this.browser.testXhr2()) {
      tItem.status = 3012;
    }
    return tItem;
  }

  private async storeData(storageMethod: IStorageMethod, dlData: IStoreData): Promise<IStoreData> {
    const packetAmt = Math.ceil(dlData.offset / this.crypt.GCM_PACKET_SIZE),
      gcmOffset = packetAmt * this.crypt.GCM_TAG_OVERHEAD;
    let writeOffset = dlData.offset - gcmOffset;
    if (writeOffset < 0) {
      writeOffset = 0;
    }
    await storageMethod.writeChunk(dlData.tItem, dlData.bytearray, writeOffset);
    return dlData;
  }

  private async processChunks(
    storageMethod: IStorageMethod,
    tItem: ITransferItemDownload,
  ): Promise<ITransferItemDownload> {
    let offset = 0;
    while (offset < tItem.enc_filesize) {
      if (storageMethod.isStreamingToDisk && this.isCancelled(tItem)) {
        await storageMethod.cancel(tItem);
        return tItem;
      }
      this.log.d(offset + ' --- < --- ' + tItem.enc_filesize);
      if (offset >= tItem.enc_filesize) {
        tItem.status = TRANSFER_STATUS.STATUS_SUCCESS;
        return tItem;
      }
      const stTime = Date.now();
      const remaining = tItem.enc_filesize - offset;
      const reqChunk = remaining > this.chunksize ? this.chunksize : remaining;
      try {
        const dlData = await this.processChunkWithRetry(storageMethod, tItem, offset, reqChunk);
        this.log.info(`processChunk ${Date.now() - stTime} ms`);

        this.log.info(
          'filesize: ' +
            tItem.enc_filesize +
            ' Offset = ' +
            offset +
            ' reqChunk = ' +
            reqChunk +
            ' -- received = ' +
            dlData.chunkLength,
        );
        // received chunk length, make next request with that offset
        offset += dlData.chunkLength;

        if (dlData.chunkLength + offset == offset) {
          this.log.error('Chunk length was 0, something has gone wrong');
          tItem.status = TRANSFER_STATUS.STATUS_ERROR;
          return tItem;
        }
        if (offset >= tItem.enc_filesize) {
          tItem.status = TRANSFER_STATUS.STATUS_SUCCESS;
          return tItem;
        } else if (tItem.blobtype.indexOf('btTHUMB') > -1) {
          // when it's a thumbnail, we don't know the size of the file.
          // MFS should always give the thumbnail in it's entirety so
          // if we got a response, we should assume it's the entire
          // thumbnail.
          return tItem;
        }
      } catch (err) {
        this.log.error(err);
        this.log.error('Error downloading chunk or storing it');
        if (storageMethod.isStreamingToDisk) {
          // processChunks returns rather than throws, so download()'s catch never sees this — the
          // session must be settled here or the partial file is left on the user's disk.
          await storageMethod.cancel(tItem, err);
          tItem.status = this.isCancellation(err, tItem)
            ? TRANSFER_STATUS.STATUS_DOWNLOAD_CANCELLED
            : this.statusFromError(err);
          return tItem;
        }
        tItem.status = this.statusFromError(err);
        return tItem;
      }
    }
    return tItem;
  }

  /** Retries the same offset: transport retry lives a layer down, this covers decrypt and storage. */
  private async processChunkWithRetry(
    storageMethod: IStorageMethod,
    tItem: ITransferItemDownload,
    offset: number,
    reqChunk: number,
  ): Promise<IStoreData> {
    for (let attempt = 0; ; attempt++) {
      try {
        const result = await this.downloader.getChunk(
          tItem,
          offset,
          reqChunk,
          0,
          this.chunkFetchOptions(storageMethod, tItem),
        );
        return await this.storeData(storageMethod, result);
      } catch (err) {
        const isLastAttempt = attempt >= DOWNLOAD_CHUNK_PROCESS_MAX_RETRIES;
        if (isLastAttempt || !isRetryableDownloadError(this.asErrCode(err))) {
          throw err;
        }
        this.log.warn(`Retrying chunk at offset ${offset} (attempt ${attempt + 1})`);
      }
    }
  }

  private isCancelled(tItem: ITransferItemDownload): boolean {
    return tItem.status === TRANSFER_STATUS.STATUS_DOWNLOAD_CANCELLED;
  }

  /** The fetch layer reports a cancel as a terminal DOWNLOAD_CANCELLED rejection, not via the item. */
  private isCancellation(err: unknown, tItem: ITransferItemDownload): boolean {
    return this.isCancelled(tItem) || (err as DownloadRejection)?.code === ERROR_CODES.DOWNLOAD_CANCELLED;
  }

  /** Only the streaming path observes cancellation; the staged path keeps its existing behaviour. */
  private chunkFetchOptions(
    storageMethod: IStorageMethod,
    tItem: ITransferItemDownload,
  ): DownloadChunkFetchOptions | undefined {
    if (!storageMethod.isStreamingToDisk) {
      return undefined;
    }
    return { isCancelled: (): boolean => this.isCancelled(tItem) };
  }

  /** Decrypt workers reject with a bare `{ errcode }`, so normalise before testing retryability. */
  private asErrCode(err: unknown): unknown {
    if (err instanceof ErrCode) {
      return err;
    }
    const errcode = (err as DownloadRejection)?.errcode;
    return errcode ? new ErrCode(errcode) : err;
  }

  /** Decrypt workers reject with `{ errcode }`; chunk fetches reject with an `ErrCode` carrying `code`. */
  private statusFromError(err: unknown): number {
    const rejection = err as DownloadRejection;
    return rejection?.errcode || rejection?.code || TRANSFER_STATUS.STATUS_ERROR;
  }

  private prepareRenderData(renderData: IRenderData): IRenderFile {
    const decStart = Date.now();
    const renderFile: IRenderFile = {
      dl_ready: true,
      type: renderData.type,
      url: renderData.url,
      savemethod: this.browser.getFileSaveMethod(),
    };
    if (renderFile.savemethod == 'msblob') {
      renderFile.msblob = renderData.blob;
    }
    this.log.d('  - ' + (Date.now() - decStart) + ' ms to prepareRenderData()');
    return renderFile;
  }
}
